import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { Layout } from "antd";

import { useConfig } from "./data/useConfig";
import { useFilters } from "./data/useFilters";
import { useSearchResults } from "./data/useSearchResults";

import useMobileDetector from "./utils/useMobileDetector";

import Filters from "./components/Filters";
import Header from "./components/Header";
import NotFound from "./components/ResultsPage/NotFound";
import ResultsPage from "./components/ResultsPage";
import SearchInput from "./components/SearchInput/Input";
import Title from "./components/Title/Title";

import "./App.less";

const { Sider, Content } = Layout;

const CustomOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: none;
  z-index: 1;
  pointer-events: none;
`;

const CustomContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 30px;

  .ant-layout-sider-zero-width-trigger {
    display: ${(props) => (!props.collapsed ? "none" : "block")};
  }

  .beon-showcase--grid .beon-showcase__item .beon-showcase__item-image img {
    width: auto !important;
    height: auto !important;
  }

  ${({ customCss }) => css`
    ${customCss}
  `}
`;

const SearchArea = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
`;

const NoTermSearch = styled.div`
  h2 {
    font-size: 20px;
    text-align: center;
    margin-top: 40px;
    margin-bottom: 40px;
  }
  margin-bottom: 40px;
`;

// layout type
// sidebar | fullwidth
const LayoutApp = ({
  layoutType = "sidebar",
  enableButton = false,
  onlyInput = false,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const { isMobile, isDesktop } = useMobileDetector();
  const {
    setId,
    tenant_id,
    customParams,
    searchPageCustomCss,
    show_sandbox_input,
    filterDirection,
  } = useConfig();
  const {
    data: filterData,
    order,
    selectedFilters,
    page,
    isLoading: isLoadingFilters,
    refetch: fetchFilters,
    getFiltersQuery,
    search,
    clearFilters,
    error,
    setData,
    changeFilter,
    initialFilters,
    cacheKey,
    setCacheKey,
    anchor,
    setSearch,
    setAnchor,
    setLoading: setLoadingFilters,
  } = useFilters();
  const {
    isLoading,
    data,
    refetch: fetchSearchResults,
    error: searchError,
    setLoading: setLoadingResults,
  } = useSearchResults();
  let { id } = useParams();

  if (!id && window.bn_config_search_page.tenant_id)
    id = window.bn_config_search_page.tenant_id;

  const closeSidebar = () => {
    setCollapsed(true);
  };

  const FULL_WIDTH = layoutType === "fullwidth";

  useEffect(() => {
    setId(id);
  }, [setId, id]);

  useEffect(() => {
    if (
      window.bn_config_search_page.search_term ||
      window.bn_config_search_page.anchors
    ) {
      if (window.bn_config_search_page.search_term) {
        setSearch({ input: window.bn_config_search_page.search_term });
      }

      if (window.bn_config_search_page.anchors) {
        setAnchor(window.bn_config_search_page.anchors);
      }
    }
  }, []);

  useEffect(() => {
    if (onlyInput) return;

    const getData = async () => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const anchors =
        urlParams.get("anchors") || window.bn_config_search_page.anchors;
      const filtersQuery = getFiltersQuery();
      const urlSortKey = urlParams.get("sort");
      const pageQuery = urlParams.get("page");

      const searchResults = await fetchSearchResults(
        tenant_id,
        search.input,
        pageQuery || page,
        filtersQuery,
        anchors,
        `&cacheKey=${cacheKey}`,
        `&sortKey=${urlSortKey || order}`
      );

      if (searchResults?.meta?.filters_available) {
        setData({
          ...filterData,
          meta: {
            ...filterData.meta,
            filters_available: searchResults.meta.filters_available,
            results_count:
              searchResults.meta.filters_results_count ||
              searchResults.meta.results_count,
            pages_count:
              searchResults.meta.filters_page_count ||
              searchResults.meta.pages_count,
            term: searchResults.meta.term,
          },
        });
        if (searchResults?.data?.cacheKey) {
          setCacheKey(searchResults?.data?.cacheKey);
        }
      }
    };
    const initFilters =
      initialFilters?.length > 0
        ? JSON.stringify(
            initialFilters.map((item) => ({
              name: item.facet,
              values: item.values,
            }))
          )
        : false;

    if (
      (!isLoadingFilters &&
        filterData?.meta?.pages_current &&
        !isLoading &&
        initFilters !== JSON.stringify(selectedFilters)) ||
      (page && !isLoading && !isLoadingFilters)
    ) {
      getData();
    }
  }, [order, selectedFilters, page]);

  const prevCustomParams = useRef();
  useEffect(() => {
    if (onlyInput) return;

    let customParamsTriggered = false;
    if (prevCustomParams.current !== customParams) customParamsTriggered = true;
    prevCustomParams.current = customParams;

    clearFilters(customParamsTriggered);

    const getData = async () => {
      const anchors = anchor;

      const normalizedAnchors = () => {
        if (!anchors) return "";
        if (typeof anchors === "string") {
          return `&anchors[]=${anchors}`;
        } else if (anchors.length > 0) {
          const nAnchors = [];
          anchors.map((anchor) => nAnchors.push(`&anchors[]=${anchor}`));
          return nAnchors.join("");
        } else {
          return "";
        }
      };

      let userCustomParams = "";
      Object.entries(customParams)?.map(
        ([paramKey, paramValue]) =>
          (userCustomParams = userCustomParams + `&${paramKey}=${paramValue}`)
      );

      const filters = await fetchFilters(
        tenant_id,
        search.input,
        normalizedAnchors(),
        userCustomParams
      );

      if (filters?.meta?.filters_applied) {
        changeFilter(
          filters.meta?.filters_applied?.map((item) => ({
            name: item.facet,
            values: item.values,
          })) || []
        );
      }

      const normalizedQuery = () => {
        if (customParamsTriggered) {
          return selectedFilters || [];
        }
        return filters?.meta?.filters_applied || [];
      };

      const searchResults = await fetchSearchResults(
        tenant_id,
        search.input,
        1,
        normalizedQuery(),
        normalizedAnchors(),
        `&cacheKey=${filters?.cacheKey}`,
        `&sortKey=${order}`
      );
      if (searchResults?.meta?.filters_available) {
        const normalizeFilterData = filterData || { meta: {} };
        setData({
          ...normalizeFilterData,
          meta: {
            ...normalizeFilterData.meta,
            filters_available: searchResults.meta.filters_available,
            results_count:
              searchResults.meta.filters_results_count ||
              searchResults.meta.results_count,
            pages_count:
              searchResults.meta.filters_page_count ||
              searchResults.meta.pages_count,
            term: searchResults.meta.term || normalizeFilterData.meta.term,
          },
        });
      }
    };

    if (search.input || anchor) {
      getData();
    }
  }, [search, anchor, customParams]);

  useEffect(() => {
    if (onlyInput) return;

    if (data?.results && data?.results.length > 0 && (search.input || anchor)) {
      if (window.beone) {
        window.beone.dispatchEvent("buddy.slider.init.after", {
          element: document.querySelector(".beon-showcase--search"),
          method: "",
        });
        window.beone.dispatchEvent(
          "buddy.elements.handle.item.inject.success",
          {
            element: document.querySelector(".beon-showcase--search"),
            container: document.querySelector(
              ".beon-showcase--search-container"
            ),
          }
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (error || searchError) {
      setLoadingFilters(false);
      setLoadingResults(false);
    }
  }, [error, searchError]);

  if (!id) return;

  if (onlyInput) return <SearchInput onSubmit={true} />;

  if (!anchor && !search.input && !window.bn_config_search_page.search_term)
    return (
      <NoTermSearch>
        <h2>Faça sua busca abaixo:</h2>
        <SearchInput onSubmit={false} noTerm={true} />
      </NoTermSearch>
    );

  if (
    filterData?.meta?.results_count === 0 ||
    data?.meta?.results_count === 0 ||
    error ||
    searchError
  ) {
    return (
      <NoTermSearch>
        <NotFound />
      </NoTermSearch>
    );
  }

  let areThereFilters = false;
  if (data?.meta?.filters_available) {
    Object.values(data.meta.filters_available)?.forEach((filter) => {
      if (Object.keys(filter.values)?.length > 1) areThereFilters = true;
    });

    if (selectedFilters.length > 0) areThereFilters = true;
  }

  const renderFilters = () => (
    <Filters
      closeSidebar={closeSidebar}
      collapsed={collapsed}
      layoutType={isMobile ? "sidebar" : layoutType}
      showLabel={FULL_WIDTH ? !isMobile : false}
      enableButton={enableButton}
    />
  );

  const renderSider = () => (
    <Sider
      theme="light"
      collapsed={collapsed}
      breakpoint="lg"
      collapsedWidth="0"
      width={isMobile ? "100vw" : 250}
      onCollapse={(collapsed) => setCollapsed(collapsed)}
    >
      {renderFilters()}
    </Sider>
  );

  return (
    <CustomContainer
      className={`bnly-search__page-container ${
        collapsed ? "bnly-sider-collapsed" : "bnly-sider-open"
      }`}
      collapsed={collapsed}
      customCss={searchPageCustomCss}
    >
      {filterDirection === "horizontal" && isDesktop && (
        <CustomOverlay className="bnly-search__overlay" />
      )}

      {show_sandbox_input && (
        <SearchArea>
          <SearchInput />
        </SearchArea>
      )}

      <Title isMobile={isMobile} />

      <Layout>
        {filterDirection !== "horizontal" &&
          areThereFilters &&
          (FULL_WIDTH && isDesktop ? renderFilters() : renderSider())}

        {filterDirection === "horizontal" &&
          (isMobile ? renderSider() : renderFilters())}

        <Content>
          <Header />
          <ResultsPage />
        </Content>
      </Layout>
    </CustomContainer>
  );
};

export default LayoutApp;
